import { useLocalObservable } from 'mobx-react-lite';
import { useCallback, useEffect, useRef } from 'react';

import { CallMemberStatus, ICallMemberChanged } from '@gem/call/Model/callModel.types';
import { wsCallsApiConnect, wsCallsApiDisconnect } from '@gem/call/WsApi/wsApi';
import { initCallsApi } from '@gem/call/WsApi/wsCallsApi/WsCallsApi';
import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { TRemoveAudioSource } from 'APP/Tasks/app/audioSource/setCurrentSource';
import { RefreshTokenInitiator } from 'APP/Tasks/authorization/login/login.types';
import { refreshToken } from 'APP/Tasks/authorization/login/refreshToken';
import { TAvatarBackgroundColorIndex } from 'APP/components/Avatar/Avatar';
import { AudioSourceType } from 'APP/constants/app';
import { callModel } from 'APP/model/model';
import { ProjectUrls } from 'APP/packages/projectUrls/ProjectUrls';
import { websocketConfig } from 'APP/wsApi/wsApi.config';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { IncomingCall } from 'STORE/IncomingCalls/IncomingCall/IncomingCall';

interface IIncomingCallAvatar {
  title: string | null;
  url: string | null;
  colorIndex: TAvatarBackgroundColorIndex | null;
}

interface IIncomingCallPresenter {
  call: IncomingCall;
  hasCall: boolean;
  group: Group | null;
  callTitle: string;
  avatar: IIncomingCallAvatar;
  disposeListener: (() => void) | null;
  isCallsSocketInitialized: boolean;
  decline(): void;
  joinToCall(): void;
  joinToCallWithVideo(): void;
  startCallsSocket(channelId: string): void;
  stopCallsSocket(): void;
  onRefreshToken(): Promise<string | null>;
  initCallsSocket(): void;
}

export function useIncomingCallPresenter(): IIncomingCallPresenter {
  const removeSourceRef = useRef<TRemoveAudioSource | null>(null);
  const { t } = useTranslation();

  const presenter = useLocalObservable<IIncomingCallPresenter>(() => ({
    disposeListener: null,
    isCallsSocketInitialized: false,
    get call(): IncomingCall {
      return Entities.IncomingCalls.calls[0];
    },

    get hasCall(): boolean {
      return Boolean(presenter.call);
    },

    get group(): Group | null {
      if (!presenter.hasCall) {
        return null;
      }

      return presenter.call.group;
    },

    get callTitle(): string {
      if (!presenter.hasCall) {
        return '';
      }

      if (presenter.group?.isGroup) {
        return presenter.call.initiatorUser
          ? `${presenter.call.initiatorUser?.avatarTitle} - ${presenter.group?.avatarTitle}`
          : t('call_guest_call_title');
      }

      return presenter.group?.avatarTitle || '';
    },

    get avatar(): IIncomingCallAvatar {
      const group = presenter.group;

      return {
        title: presenter.call.initiatorUser ? group?.avatarTitle || '' : t('call_guest_call_title'),
        url: group?.avatarUrl || null,
        colorIndex: group?.avatarColorIndex || null,
      };
    },

    decline(): void {
      Tasks.calls.decline({
        groupId: presenter.group?.id || null,
        channelId: presenter.call.channelId,
      });
    },

    joinToCall(): void {
      Tasks.calls.joinToCall({
        groupId: presenter.group?.id || null,
        channelId: presenter.call.channelId,
        withAudio: !!presenter.group?.isP2P,
        withVideo: false,
        providerType: presenter.call.callProvider,
      });
    },

    joinToCallWithVideo(): void {
      Tasks.calls.joinToCall({
        groupId: presenter.group?.id || null,
        channelId: presenter.call.channelId,
        withAudio: !!presenter.group?.isP2P,
        withVideo: true,
        providerType: presenter.call.callProvider,
      });
    },

    initCallsSocket(): void {
      initCallsApi(ProjectUrls.wsCallsUrl);
      presenter.disposeListener = callModel.initCallListener({
        channelId: presenter.call.channelId,
        onUserCallData(data: ICallMemberChanged) {
          console.log(
            'INCOMING_CALL',
            '6 IncomingCall popup socket "callMemberChanged" event ',
            JSON.stringify({
              data: data,
              memberId: data.memberId,
              status: data.status,
            })
          );
          if (
            data.memberId === Entities.usersStore.Me.id &&
            [CallMemberStatus.OnCall, CallMemberStatus.Declined].includes(data.status)
          ) {
            Entities.IncomingCalls.delete(presenter.call.channelId);
          }
        },
      });

      presenter.startCallsSocket(presenter.call.channelId);
    },

    startCallsSocket(channelId: string): void {
      wsCallsApiConnect({
        session: websocketConfig.token || '',
        channelId,
        wsCallsUrl: ProjectUrls.wsCallsUrl,
        refreshToken: async () => presenter.onRefreshToken(),
      });
      presenter.isCallsSocketInitialized = true;
    },

    async onRefreshToken(): Promise<string | null> {
      const result = await refreshToken(false, RefreshTokenInitiator.WsCalls);

      if (result.isSuccess && result.token) {
        return result.token;
      } else {
        return null;
      }
    },

    stopCallsSocket(): void {
      if (presenter.disposeListener) {
        presenter.disposeListener();
      }
      wsCallsApiDisconnect();
      presenter.isCallsSocketInitialized = false;
    },
  }));

  const removeAudioSource = useCallback(() => {
    if (removeSourceRef.current) {
      removeSourceRef.current();
      removeSourceRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (presenter.hasCall) {
      // close all popover
      document.dispatchEvent(new Event('click'));

      removeSourceRef.current = Tasks.app.audioSource.setCurrentSource(
        AudioSourceType.IncomingCall
      );
    } else {
      removeAudioSource();
    }
  }, [presenter.hasCall]);

  useEffect(() => {
    return (): void => removeAudioSource();
  }, []);

  useEffect(() => {
    if (presenter.call?.channelId) {
      presenter.initCallsSocket();
    }
    return () => {
      if (presenter.isCallsSocketInitialized) {
        presenter.stopCallsSocket();
      }
    };
  }, [presenter.call?.channelId]);

  useEffect(() => {
    console.log('INCOMING_CALL', '4 Find incoming calls');
  }, [Entities.IncomingCalls.calls]);

  return presenter;
}
